import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'
import { PageTitle } from '../components/atoms/PageTitle'
import { HistoryStats } from '../components/HistoryStats'
import { PortHistory } from '../components/PortHistory'
import { PriceHistory } from '../components/PriceHistory'
import { useAnchor } from '../hooks/useAnchor'
import { callAxios, useAxios } from '../hooks/useAxios'
import { ApplianceData } from '../classes/appliances/Appliance'
import { useApp } from '../hooks/useApp'
import { Box, FormControlLabel, Switch } from '@mui/material'
import { useNoRefresh } from '../hooks/useRefresh'
import { useAppliancePorts } from '../hooks/useAppliancePorts'
import { PageText } from '../components/atoms/PageText'
import { HeatPumpHistory } from '../components/HeatPumpHistory'

export function HistoryPage(): React.ReactElement {

  useAnchor()

  const { t } = useTranslation()
  const [appliances, setAppliances] = useState<ApplianceData[]>([])
  const app = useApp()
  useNoRefresh()
  const { items, syncing } = useAppliancePorts(appliances)

  // Show only first for now. Later this could be selected using the `page` concept.
  const location = app.locations.length ? app.locations[0] : null

  // Proof of concept: the fee switch only for the HEAT_PUMP_HISTORY feature; without it the figures stay without fees.
  const canIncludeFees = app.userHas('HEAT_PUMP_HISTORY')
  const includeFees = canIncludeFees && app.user?.settings?.historyIncludeFees === true
  // The customer's choice, kept in their settings: shown at once, taken back if saving fails.
  const changeFees = async (wanted: boolean) => {
    const user = app.user
    if (!user) return
    app.setUser({ ...user, settings: { ...user.settings, historyIncludeFees: wanted } })
    const res = await callAxios({ url: '/auth/me', method: 'PATCH' }, { settings: { historyIncludeFees: wanted } })
    if (!res.success) {
      app.setUser(user)
      app.addError(res)
    }
  }

  useAxios<ApplianceData[]>({
    url: `/appliances?location=${location && location.id}`,
    onSuccess: data => {
      setAppliances(data.filter(a => a.type === 'ENERGY_METER' || a.type === 'SOLAR_PANEL' || a.type === 'FCR_BATTERY' || a.type === 'BATTERY' ||
        // Proof of concept: heat pump history only for the HEAT_PUMP_HISTORY feature.
        (a.type === 'HEAT_PUMP' && app.userHas('HEAT_PUMP_HISTORY'))))
    }
  })

  return (
    <>
      <TopTitle title={t('History')}/>
      {canIncludeFees && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FormControlLabel
          data-cy="history-include-fees"
          control={<Switch checked={includeFees} onChange={event => changeFees(event.target.checked)}/>}
          label={t('Include transfer fee and margin')}
        />
      </Box>}
      {
        items.map(({ appliance, portControl }) => <Box id={`port${appliance.ports[0]}`} key={appliance.id} sx={{ mb: 6 }}>
          <PageTitle sx={{ mb: 2 }} title={`${appliance.name} - ${t(`Appliance-${appliance.type}`)}`} />
          {portControl
            ? <>
              {(appliance.type === 'ENERGY_METER' || appliance.type === 'SOLAR_PANEL') && <>
                <PageTitle sx={{ mb: 2 }} title={t('Stats')}/>
                <HistoryStats port={portControl.port} includeFees={includeFees}/>
                <PageTitle sx={{ mt: 4, mb: 2 }} title={appliance.type === 'SOLAR_PANEL' ? t('Production') : t('Consumption') }/>
              </>}
              {appliance.type === 'HEAT_PUMP'
                ? <HeatPumpHistory port={portControl.port} includeFees={includeFees}/>
                : <PortHistory variant={appliance.type === 'SOLAR_PANEL' ? 'primary' : 'secondary'} device={portControl.device} port={portControl.port}/>}
            </>
            : !syncing && <PageText>{t('The port of the appliance was not found. Please check your devices.')}</PageText>}
        </Box>)
      }

      <PageTitle id="MarketPrice" sx={{ mt: 4, mb: 2 }} title={t('Market Prices')}/>
      <PriceHistory/>
    </>
  )
}
